import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { Section } from '../components/templates/Section/Section';
import { Button } from '../components/atoms/Button/Button';
import { Stack } from '../components/atoms/Stack/Stack';
import { OverviewUploadStep } from '../components/livestock/LivestockOverview/OverviewUploadStep';
import { OverviewPreviousAnalyses } from '../components/livestock/LivestockOverview/OverviewPreviousAnalyses';
import { OverviewAnalysisView } from '../components/livestock/LivestockOverview/OverviewAnalysisView';
import { livestockKeys } from '../hooks/useLivestock';
import { livestockOverviewKeys } from '../hooks/useLivestockOverview';
import { useTranslation } from '../i18n/useTranslation';
import type { OverviewAnalysis, OverviewCreateResult } from '../services/livestockOverviewService';

/** Navigation state the livestock list reads to show the summary after creating animals. */
export interface OverviewResultState {
  overviewResult: { created: number; photoAttachFailed: number };
}

/**
 * "Analyse an overview photo" — /aquariums/:aquariumId/livestock/overview (upload) and
 * /aquariums/:aquariumId/livestock/overview/:analysisId (progress → review). The analysis
 * id is in the URL, so a refresh or coming back later shows the same result.
 */
export function LivestockOverviewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { aquariumId = '', analysisId } = useParams<{ aquariumId: string; analysisId?: string }>();
  const livestockPath = `/aquariums/${aquariumId}/livestock`;
  const uploadPath = `${livestockPath}/overview`;

  const openAnalysis = useCallback(
    (id: string) => navigate(`${uploadPath}/${id}`),
    [navigate, uploadPath],
  );

  const handleStarted = useCallback(
    (analysis: OverviewAnalysis) => {
      queryClient.setQueryData(livestockOverviewKeys.detail(aquariumId, analysis.id), analysis);
      void queryClient.invalidateQueries({ queryKey: livestockOverviewKeys.list(aquariumId) });
      openAnalysis(analysis.id);
    },
    [aquariumId, openAnalysis, queryClient],
  );

  const handleNewPhoto = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: livestockOverviewKeys.all(aquariumId) });
    navigate(uploadPath);
  }, [aquariumId, navigate, queryClient, uploadPath]);

  const handleCreated = useCallback(
    (result: OverviewCreateResult) => {
      void queryClient.invalidateQueries({ queryKey: livestockKeys.all(aquariumId) });
      void queryClient.invalidateQueries({ queryKey: livestockOverviewKeys.all(aquariumId) });
      const state: OverviewResultState = {
        overviewResult: { created: result.created.length, photoAttachFailed: result.photoAttachFailed },
      };
      navigate(livestockPath, { state });
    },
    [aquariumId, livestockPath, navigate, queryClient],
  );

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="default" data-testid="livestock-overview-page">
          <PageHeader
            title={t('livestockOverview.title')}
            description={t('livestockOverview.subtitle')}
            back={
              <Button variant="ghost" onClick={() => navigate(livestockPath)}>
                {t('livestockOverview.back')}
              </Button>
            }
          />
          {analysisId ? (
            <OverviewAnalysisView
              aquariumId={aquariumId}
              analysisId={analysisId}
              onNewPhoto={handleNewPhoto}
              onCreated={handleCreated}
            />
          ) : (
            <Stack gap="8">
              <Section title={t('livestockOverview.upload.sectionTitle')}>
                <OverviewUploadStep aquariumId={aquariumId} onStarted={handleStarted} />
              </Section>
              <OverviewPreviousAnalyses aquariumId={aquariumId} onOpen={openAnalysis} />
            </Stack>
          )}
        </PageContent>
      }
    />
  );
}

export default LivestockOverviewPage;
